import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SocialShare from '../components/SocialShare';
import { blogAPI, getImageUrl } from '../services/api';
import { Calendar, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { blogJsonLd } from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchPost(); }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await blogAPI.getBySlug(slug);
      setPost(response.data);
    } catch (err) {
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (<div className="min-h-screen bg-white dark:bg-gray-900"><Header /><div className="container mx-auto px-4 py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div></div>);
  }

  if (error || !post) {
    return (<div className="min-h-screen bg-white dark:bg-gray-900"><Header /><div className="container mx-auto px-4 py-16 text-center"><h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Post Not Found</h1><Link to="/blog"><Button variant="outline">Back to Blog</Button></Link></div></div>);
  }

  const postUrl = `https://allseoconferences.com/blog/${slug}`;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title={post.title}
        description={post.excerpt || post.metaDescription || post.title}
        canonical={postUrl}
        type="article"
        image={getImageUrl(post.coverImage)}
        jsonLd={blogJsonLd(post, postUrl)}
      />
      <Header />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
          <img src={getImageUrl(post.coverImage)} alt={post.title} className="w-full h-96 object-cover rounded-lg mb-8" />
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="w-4 h-4 mr-2" /> {formatDate(post.publishedAt)}
            </div>
            <SocialShare title={post.title} url={postUrl} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-8">{post.title}</h1>
          <div className="prose prose-lg dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Enjoyed this article? Share it!</p>
            <SocialShare title={post.title} url={postUrl} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPost;
